import React from 'react';
import { ComponentTypeNames } from '../../../schema';
import { DatoCmsListComponent, ListItems } from '../../../schema/datocms';
import { DatoCmsContactInfoListItem, DatoCmsImageListItem, DatoCmsInformationCardListItem, DatoCmsLinkListItem, DatoCmsNumberedListItem, DatoCmsPageListItem, DatoCmsProductListItem, DatoCmsProductTileListItem, DatoCmsStatisticListItem } from '../../../schema/datocms/blocks';
import { getColumnSpanClass, getColumnStartClass, getFlexJustification, getTextAlignmentClass } from '../../../utilities';
import ContactInfoListItemComponent from '../../block-components/contact-info-list-item-component/contact-info-list-item-component';
import ImageListItemComponent from '../../block-components/image-list-item-component/image-list-item-component';
import InformationCardItemComponent from '../../block-components/information-card-list-item-component/information-card-list-item-component';
import LinkListItemComponent from '../../block-components/link-list-item-component/link-list-item-component';
import NumberedListItemComponent from '../../block-components/numbered-list-item-component/numbered-list-item-component';
import PageListItemComponent from '../../block-components/page-list-item-component/page-list-item-component';
import ProductListItemComponent from '../../block-components/product-list-item-component/product-list-item-component';
import ProductTileListItemComponent from '../../block-components/product-tile-list-item-component/product-tile-list-item-component';
import StatisticListItemComponent from '../../block-components/statistic-list-item-component/statistic-list-item-component';
import Button from '../../button/button';
import HtmlStringComponent from '../../html-string/html-string-component';
import { useThemeClassMap } from '../../theme/useThemeClassMap';

const ListComponent = (
    { title, descriptionHtml, theme, listItems, button, buttonAlignment, maximumItemsPerRow,
        titleAlignment, columnLayout, additionalTextHtml, listItemBackgroundColor
    }: DatoCmsListComponent
): React.ReactElement => {

    const themeClass = useThemeClassMap(theme);
    let sourceCount: number = 0;
    const listClass: string = listItemBackgroundColor? 'list-items-colored' : '';

    function listItemMapper(block: ListItems, index: number): React.ReactElement | null {
        switch (block.__typename) {
            case ComponentTypeNames.NUMBERED_LIST_ITEM:
                return <NumberedListItemComponent key={index} displayIndex={index + 1} item={block as DatoCmsNumberedListItem} />;
            case ComponentTypeNames.STATISTIC_LIST_ITEM:
                const statItem: DatoCmsStatisticListItem = block as DatoCmsStatisticListItem;
                const includeSourceCount: boolean = Boolean(statItem && statItem.source);
                if (includeSourceCount) {
                    sourceCount += 1;
                }
                return <StatisticListItemComponent key={index} item={block as DatoCmsStatisticListItem} sourceIndex={includeSourceCount ? sourceCount : undefined} />;
            case ComponentTypeNames.IMAGE_LIST_ITEM:
                return <ImageListItemComponent key={index} {...block as DatoCmsImageListItem} />;
            case ComponentTypeNames.INFO_CARD_LIST_ITEM:
                return <InformationCardItemComponent key={index} {...block as DatoCmsInformationCardListItem} />;
            case ComponentTypeNames.LINK_LIST_ITEM:
                return <LinkListItemComponent key={index} {...block as DatoCmsLinkListItem} />;
            case ComponentTypeNames.PAGE_LIST_ITEM:
                return <PageListItemComponent key={index} {...block as DatoCmsPageListItem} />;
            case ComponentTypeNames.PRODUCT_LIST_ITEM:
                return <ProductListItemComponent key={index} {...block as DatoCmsProductListItem} />;
            case ComponentTypeNames.PRODUCT_TILE_LIST_ITEM:
                return <ProductTileListItemComponent key={index} {...block as DatoCmsProductTileListItem} />;
            case ComponentTypeNames.CONTACT_INFO_LIST_ITEM:
                return <ContactInfoListItemComponent key={index} {...block as DatoCmsContactInfoListItem} />;
            default:
                console.warn('Unsupported List Item', block.__typename);
                return null;
        }
    }

    const gridColumnSpanClass: string = getColumnSpanClass(columnLayout);
    const gridColumnStartClass: string = getColumnStartClass(columnLayout);

    // product lists need extra spacing between items;
    const yGapClass: string = listItems.some((l) => l.__typename === ComponentTypeNames.PRODUCT_LIST_ITEM) ? 'gap-y-24' : 'gap-y-8';

    // product tiles need to be sorted by the product sort order, this can't be done in a graphql query
    listItems.sort((a, b) => {
        if (a.__typename === ComponentTypeNames.PRODUCT_TILE_LIST_ITEM && b.__typename === ComponentTypeNames.PRODUCT_TILE_LIST_ITEM) {
            const aProductTile: DatoCmsProductTileListItem = a as DatoCmsProductListItem;
            const bProductTile: DatoCmsProductTileListItem = b as DatoCmsProductListItem;

            return aProductTile.product.position - bProductTile.product.position;
        }

        return 0;
    });

    return (
        <div className={`${ themeClass } page-component`}>
            <div className="container mx-auto lg:grid lg:grid-cols-12 px-3 lg:px-6">
                <div className={`lg:${gridColumnSpanClass} lg:${gridColumnStartClass}`}>
                    { title && <h2 className={`text-center md:${getTextAlignmentClass(titleAlignment)}`}>{title}</h2> }

                    { descriptionHtml && <HtmlStringComponent htmlString={descriptionHtml} skipAlignmentClasses={true} /> }

                    <div className={`grid grid-cols-1 lg:grid-cols-${maximumItemsPerRow} ${yGapClass} gap-x-8 mt-8 ${listClass}`}>
                        {
                            listItems.map((block, index) => listItemMapper(block, index))
                        }
                    </div>

                    { additionalTextHtml && <HtmlStringComponent htmlString={additionalTextHtml} skipAlignmentClasses={true} /> }

                    {

                        button && button.length > 0 &&
                    <div className={`mt-12 flex justify-center md:${getFlexJustification(buttonAlignment)}`}>
                        <Button {...button[0]} className='text-center'/>
                    </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default ListComponent;
